import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Drawer from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Drawer';
import { Link } from 'react-router-dom';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import List from '@material-ui/core/List';
import LogoutIcon from 'Icons/Logout';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useUserContext from 'Context/UserContext';
import useEventTrackingLogEvent, { eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { useLayoutContext } from 'Context/LayoutContext';
import { DRAWER_WIDTH_OPEN, DRAWER_WIDTH_CLOSED } from 'Components/UI/Navigation/Layouts/DesktopLayout';
import useHomeContext from 'Context/HomeContext';
import FacetLogoPrimary from 'Icons/Brand/FacetLogoPrimary';
import { useLogoutContext } from 'Context/LogoutContext';
import { isHidden, checkDisabled } from '../utils';
import NavItem from '../Item';

const styles = theme => ({
  root: {
    whiteSpace: 'nowrap',
  },
  paper: {
    backgroundColor: theme.palette.common.white,
    width: DRAWER_WIDTH_CLOSED,
    color: theme.palette.facet.primaryGray3,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  paperOpen: {
    width: DRAWER_WIDTH_OPEN,
  },
  paperClosed: {
    overflowX: 'hidden',
  },
  paddingLeft: {
    paddingLeft: theme.spacing(2),
  },
  toolbar: {
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toolbarStack: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: `${theme.spacing(2)}px`,
    flexGrow: 1,
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
  facetIcon: {
    width: '115px',
    height: 'auto',
    animation: `$fadeIn 1000ms ${theme.transitions.easing.easeInOut}`,
    paddingLeft: theme.spacing(2),
  },
  icon: {
    color: theme.palette.facet.primaryGray3,
    '&:hover': {
      color: theme.palette.facet.primaryBlue,
    },
  },
});

const LeftDrawer = ({
  className,
  classes,
  schema,
}) => {
  const { householdResource } = useSimpleHousehold();
  const { userResource, agentResource } = useUserContext();
  const { logout } = useLogoutContext();
  const { rootNodeProps, logEvent, logStandardizedEvent } = useEventTrackingLogEvent();
  const { homeResource } = useHomeContext();

  const {
    isNavigationOpen,
    setNavigationStayOpen,
    setNavigationOpen,
  } = useLayoutContext();

  const onNavItemClick = (navItem) => {
    if (navItem.logEvent) {
      logStandardizedEvent(navItem.eventLoggingCategory,
        eventTypes.navigateTo,
        `${navItem.label} -  Navigated to ${navItem.label} via left nav bar on Desktop`);
    }
  };

  return (
    <Drawer
      {...rootNodeProps}
      variant="permanent"
      classes={{
        root: classNames(classes.root, className),
        paper: classNames(classes.paper, {
          [classes.paperOpen]: isNavigationOpen,
          [classes.paperClosed]: !isNavigationOpen,
        }),
      }}
    >
      <div className={classes.toolbar}>
        {isNavigationOpen ? (
          <div className={classes.toolbarStack}>
            <Link to={schema.homeLinkPath}>
              <FacetLogoPrimary className={classes.facetIcon} />
            </Link>
            <IconButton
              className={classNames(classes.icon, classes.close)}
              color="inherit"
              onClick={() => {
                logEvent('Clicked < to close');
                setNavigationOpen(false);
                setNavigationStayOpen(false);
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          </div>
        ) : (
          <IconButton
            className={classes.icon}
            color="inherit"
            aria-label="open drawer"
            onClick={() => {
              logEvent('Clicked Hamburger Button to open');
              setNavigationOpen(true);
              setNavigationStayOpen(true);
            }}
            test="navigation-hamburger-button"
          >
            <MenuIcon />
          </IconButton>
        )}
      </div>
      <List
        disablePadding
      >
        {
          schema.navigationItems.filter(isHidden({
            householdResource, userResource, agentResource, homeResource,
          })).map((navItem) => {
            const { disabled, ...restNavItem } = navItem;

            return (
              <NavItem
                key={navItem.key}
                id={navItem.id}
                disabled={checkDisabled(navItem, { householdResource, userResource })}
                drawerOpen={isNavigationOpen}
                agentResource={agentResource}
                onClick={() => onNavItemClick(navItem)}
                {...restNavItem}
              />
            );
          })
        }
        <NavItem
          variant="onClick"
          label="Logout"
          test="sign-out"
          icon={LogoutIcon}
          onClick={() => {
            logout();
          }}
        />
      </List>
    </Drawer>
  );
};

LeftDrawer.defaultProps = {
  className: '',
};

LeftDrawer.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  schema: PropTypes.object.isRequired,
};

export default withStyles(styles)(LeftDrawer);
